import scootLogo from "../assets/logo2.png";
import scootNavigationLogo from "../assets/scoot.png";
import deliDriveLogo from "../assets/deli-drive-logo.png";

/**
 * @typedef {'scoot' | 'deliDrive'} BrandKey
 * @typedef {Object} ThemeTokens
 * @property {string} primary
 * @property {string} secondary
 * @property {string} navigation
 * @property {string} background
 * @property {string} surface
 * @property {string} text
 * @property {string} mutedText
 * @property {string} border
 * @property {string} success
 * @property {string} warning
 * @property {string} danger
 * @typedef {Object} BrandConfig
 * @property {BrandKey} key
 * @property {string} displayName
 * @property {string} logo
 * @property {string} navigationLogo
 * @property {string} navigationLogoClassName
 * @property {string} favicon
 * @property {ThemeTokens} theme
 * @property {string} apiBaseUrl
 * @property {string} identityApiUrl
 * @property {Readonly<Record<string, boolean>>} features
 */

const trimTrailingSlash = (value) => value.replace(/\/$/, "");

/** @type {Readonly<Record<BrandKey, BrandConfig>>} */
export const brands = Object.freeze({
  scoot: Object.freeze({
    key: "scoot",
    displayName: "Scoot",
    logo: scootLogo,
    navigationLogo: scootNavigationLogo,
    navigationLogoClassName: "w-25 h-20 object-contain",
    favicon: scootLogo,
    theme: Object.freeze({
      primary: "#FFA629",
      secondary: "#519EAB",
      navigation: "#FFFFFF",
      background: "#F7F8FA",
      surface: "#FFFFFF",
      text: "#111827",
      mutedText: "#9CA3AF",
      border: "#E5E5EF",
      success: "#3E9E17",
      warning: "#FFA629",
      danger: "#C21919",
    }),
    apiBaseUrl: "https://sco-ot.com/api8092/api",
    identityApiUrl: "https://sco-ot.com/api8093/api",
    features: Object.freeze({}),
  }),
  deliDrive: Object.freeze({
    key: "deliDrive",
    displayName: "Deli Drive",
    logo: deliDriveLogo,
    navigationLogo: deliDriveLogo,
    navigationLogoClassName: "w-25 h-12 mt-5 object-contain",
    favicon: deliDriveLogo,
    theme: Object.freeze({
      primary: "#001B4D",
      secondary: "#FF5A00",
      navigation: "#001B4D",
      background: "#F7F8FA",
      surface: "#FFFFFF",
      text: "#111827",
      mutedText: "#6B7280",
      border: "#E5E7EB",
      success: "#3E9E17",
      warning: "#FF5A00",
      danger: "#C21919",
    }),
    apiBaseUrl: "https://sco-ot.com/api8092/api",
    identityApiUrl: "https://sco-ot.com/api8093/api",
    features: Object.freeze({}),
  }),
});

const requestedBrand = process.env.REACT_APP_BRAND || "scoot";

if (!Object.prototype.hasOwnProperty.call(brands, requestedBrand)) {
  throw new Error(`Unknown REACT_APP_BRAND "${requestedBrand}". Expected one of: ${Object.keys(brands).join(", ")}.`);
}

const selectedBrand = brands[requestedBrand];

/** @type {BrandConfig} */
export const brand = Object.freeze({
  ...selectedBrand,
  apiBaseUrl: trimTrailingSlash(process.env.REACT_APP_API_BASE_URL || selectedBrand.apiBaseUrl),
  identityApiUrl: trimTrailingSlash(process.env.REACT_APP_IDENTITY_API_URL || selectedBrand.identityApiUrl),
});

export const apiUrl = (path = "") => `${brand.apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
export const identityApiUrl = (path = "") => `${brand.identityApiUrl}${path.startsWith("/") ? path : `/${path}`}`;

const realtimeUrl = (path) => {
  const base = new URL(brand.apiBaseUrl);
  base.pathname = base.pathname.replace(/\/api\/?$/, "") + path;
  return base;
};

export const sockJsUrl = (path = "/ws-message") => {
  return realtimeUrl(path).toString().replace(/\/$/, "");
};

export const websocketUrl = (path = "/ws-message") => {
  const base = realtimeUrl(path);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  return base.toString().replace(/\/$/, "");
};
